import { of } from "@nozbe/watermelondb/utils/rx";
import {
  withObservables,
  ExtractedObservables,
} from "@nozbe/watermelondb/react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Card,
  FlatActionList,
  FlatActionListItem,
  Page,
  Stack,
} from "@towersystems/roam-common-native";
import {
  AccountFeatureFlag,
  SaleAdjustmentType,
} from "@towersystems/roam-common/lib/generated-types";
import { compose } from "recompose";
import { ActionsRootParamList } from "../../../../types";
import Sale from "../../../../utilities/data/model/Sale";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { useLayoutProvider } from "../../utils/layout";
import { withSaleController } from "../../utils/sale-controller";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useActionBackOrClose } from "../../utils/use-action-back-or-close";
import Constants from "expo-constants";
import { useTranslation } from "../../../../utilities/i18n";
import { useApplyTradePriceToSaleItems } from "../../utils/sale-item";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

export interface MoreActionsScreenProps
  extends ExtractedObservables<ReturnType<typeof getObserables>>,
    NativeStackScreenProps<ActionsRootParamList, "MoreActionsScreen"> {
  sale?: Sale;
}

const getObserables = ({ sale }: { sale: Sale }) => ({
  items: sale ? sale.items.observeWithColumns(["adjustments"]) : of(null),
});

export const MoreActionsScreenRenderer = ({
  navigation,
  sale,
  items: saleItems,
}: MoreActionsScreenProps) => {
  const { t } = useTranslation("pos");
  const { startEditing } = useLayoutProvider();
  const { settings } = useLocalSettingsProvider();
  const { applyTradePriceToSaleItems } = useApplyTradePriceToSaleItems();
  const { currentVendor } = useCurrentVendorProvider();
  const online = useOnline();
  useActionBackOrClose(navigation);

  const hasSale = Boolean(sale);
  const items: FlatActionListItem[] = [];

  const saleStateModifiers: FlatActionListItem[] = [];
  const selfCheckoutDisabled =
    Constants.expoConfig?.extra?.selfCheckoutDisabled;

  const handleApplyTradePrices = async () => {
    if (sale) {
      try {
        await applyTradePriceToSaleItems(sale);
        navigation.goBack();
      } catch (err) {}
    }
  };

  if (sale) {
    const hasCustomer = sale.customer.id;

    const isRemoveDiscount = saleItems?.some((item) =>
      item.adjustments.find((i) => i.type === SaleAdjustmentType.SALE_DISCOUNT)
    );

    const isRemoveTradePrices = saleItems?.some((item) =>
      item.adjustments.find(
        (i) => i.type === SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
      )
    );

    const isRemoveGiftVoucherCode = !!sale.giftVoucherCode;

    if (hasCustomer) {
      items.push({
        key: "remove_customer",
        title: t("action.remove_customer.label"),
        subTitle: t("action.remove_customer.description"),
        destructive: true,
        disabled: !sale.canRemoveCustomer,
        onPress: () => {
          navigation.goBack();
          sale.removeCustomer().catch((e) => console.log(e));
        },
      });
    } else {
      items.push({
        key: "add_customer",
        title: t("action.add_customer.label"),
        subTitle: t("action.add_customer.description"),
        onPress: () => {
          navigation.navigate("CustomerRootScreen", {
            screen: "CustomerListScreen",
          });
        },
      });
    }

    if (isRemoveDiscount) {
      items.push({
        key: "remove_sale_discount",
        title: t("action.remove_sale_discount.label"),
        subTitle: t("action.remove_sale_discount.description"),
        destructive: true,
        onPress: () => {
          navigation.goBack();
          sale.removeSaleDiscount().catch((e) => console.log(e));
        },
      });
    } else {
      items.push({
        key: "add_sale_discount",
        title: t("action.add_sale_discount.label"),
        subTitle: t("action.add_sale_discount.description"),
        disabled: saleItems?.length === 0 || isRemoveTradePrices,
        onPress: () => {
          navigation.navigate("DiscountSaleScreen");
        },
      });
    }

    if (isRemoveTradePrices) {
      items.push({
        key: "remove_trade_prices",
        title: t("action.remove_trade_prices.label"),
        subTitle: t("action.remove_trade_prices.description"),
        destructive: true,
        onPress: () => {
          navigation.goBack();
          sale.removeSaleTradePriceAdjustments().catch((e) => console.log(e));
        },
      });
    } else {
      items.push({
        key: "apply_trade_prices",
        disabled: saleItems?.length === 0,
        title: t("action.apply_trade_prices.label"),
        subTitle: t("action.apply_trade_prices.description"),
        onPress: handleApplyTradePrices,
      });
    }

    if (
      vendorCanAccessFeature(
        AccountFeatureFlag.POS_GIFT_VOUCHER_UI,
        currentVendor
      )
    ) {
      if (isRemoveGiftVoucherCode) {
        items.push({
          key: "remove_gift_voucher_code",
          title: t("action.remove_gift_voucher_code.label"),
          subTitle: t("action.remove_gift_voucher_code.description"),
          destructive: true,
          onPress: () => {
            navigation.goBack();
            sale.removeGiftVoucherRedemptions().catch((e) => console.log(e));
          },
        });
      } else {
        items.push({
          key: "add_gift_voucher_code",
          title: t("gift_voucher:action.add_gift_voucher_code.label"),
          subTitle: t("gift_voucher:action.add_gift_voucher_code.description"),
          disabled: true,
          onPress: () => {
            navigation.navigate("AddGiftVoucherRootScreen", {
              screen: "AddGiftVoucherScreen",
            });
          },
        });
      }
    }

    saleStateModifiers.push({
      key: "Park Sale",
      title: t("action.park_sale.label"),
      subTitle: t("action.park_sale.description"),
      onPress: () => {
        navigation.navigate("ParkScreen");
      },
    });
  }

  if (settings.allowLayoutEdit && online) {
    items.push({
      key: "Edit Layout",
      title: t("action.edit_layout.label"),
      subTitle: t("action.edit_layout.description"),
      onPress: () => {
        navigation.goBack();
        startEditing();
      },
    });
  }

  if (hasSale) {
    items.push({
      key: "edit_purchase_order_number",
      title: t("action.edit_purchase_order_number.label"),
      subTitle: t("action.edit_purchase_order_number.description"),
      onPress: () => {
        navigation.navigate("EditPurchaseOrderNumberScreen");
      },
    });
  }

  saleStateModifiers.push({
    key: "Switch Sale",
    title: t("action.switch_sale.label"),
    subTitle: t("action.switch_sale.description"),
    onPress: () => {
      navigation.navigate("SwitchSaleScreen");
    },
  });

  if (sale) {
    saleStateModifiers.push({
      key: "Edit Note",
      title: sale.note
        ? t("action.edit_note.label")
        : t("action.add_note.label"),
      subTitle: sale.note
        ? t("action.edit_note.description")
        : t("action.add_note.description"),
      onPress: () => {
        navigation.navigate("EditSaleNoteScreen");
      },
    });

    saleStateModifiers.push({
      key: "Cancel Sale",
      title: t("action.cancel_sale.label"),
      subTitle: t("action.cancel_sale.description"),
      destructive: true,
      onPress: () => {
        navigation.navigate("CancelSaleScreen");
      },
    });
  }

  const otherActions: FlatActionListItem[] = [];

  if (
    vendorCanAccessFeature(AccountFeatureFlag.POS_APP_QUOTES_UI, currentVendor)
  ) {
    otherActions.push({
      key: "Create Quote",
      title: t("action.create_quote.label"),
      subTitle: t("action.create_quote.description"),
      onPress: () => {
        navigation.navigate("ActionCreateQuoteSaleRootScreen", {
          screen: "CustomerRootScreen",
          params: { screen: "CustomerListScreen" },
        });
      },
    });
  }

  if (
    vendorCanAccessFeature(
      AccountFeatureFlag.POS_APP_INVOICES_UI,
      currentVendor
    )
  ) {
    otherActions.push({
      key: "Create Invoice",
      title: t("action.create_invoice.label"),
      subTitle: t("action.create_invoice.description"),
      onPress: () => {
        navigation.navigate("ActionCreateInvoiceSaleRootScreen", {
          screen: "CustomerRootScreen",
          params: { screen: "CustomerListScreen" },
        });
      },
    });
  }

  if (
    vendorCanAccessFeature(
      AccountFeatureFlag.POS_APP_PROFORMA_SALES_UI,
      currentVendor
    )
  ) {
    otherActions.push({
      key: "proforma",
      title: t("action.create_proforma_sale.label"),
      subTitle: t("action.create_proforma_sale.description"),
      onPress: () => {
        navigation.navigate("ActionCreateProformaSaleRootScreen", {
          screen: "ListProformasScreen",
        });
      },
    });
  }

  if (
    vendorCanAccessFeature(
      AccountFeatureFlag.POS_TRANSFER_SELF_SERVICE_UI,
      currentVendor
    ) &&
    !selfCheckoutDisabled
  ) {
    otherActions.push({
      key: "self_serve",
      title: t("action.transfer_self_serve.label"),
      subTitle: t("action.transfer_self_serve.description"),
      disabled: true,
      onPress: () => {
        navigation.navigate("TransferSelfCheckoutSaleScreen");
      },
    });
  }
  if (
    vendorCanAccessFeature(
      AccountFeatureFlag.POS_TRANSFER_RETAILER_UI,
      currentVendor
    ) &&
    hasSale
  ) {
    otherActions.push({
      key: "transfer_sale_retailer",
      title: t("action.transfer_retailer.label"),
      subTitle: t("action.transfer_retailer.description"),
      onPress: () => {
        navigation.navigate("TransferSaleRetailerScreen");
      },
    });
  }

  if (items.length <= 1 && saleStateModifiers.length <= 1) {
    return (
      <Page>
        <Stack vertical>
          <Card>
            <FlatActionList
              items={[...items, ...saleStateModifiers, ...otherActions]}
            />
          </Card>
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <Stack vertical>
        {items.length >= 1 ? (
          <Card>
            <FlatActionList items={items} />
          </Card>
        ) : null}
        {saleStateModifiers.length >= 1 ? (
          <Card>
            <FlatActionList items={saleStateModifiers} />
          </Card>
        ) : null}
        {otherActions.length >= 1 ? (
          <Card>
            <FlatActionList items={otherActions} />
          </Card>
        ) : null}
      </Stack>
    </Page>
  );
};

export const MoreActionsScreen = compose(
  withSaleController,
  withObservables(["sale"], getObserables)
)(MoreActionsScreenRenderer as any);
